import { zodResolver } from "@hookform/resolvers/zod";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { useAtom } from "jotai/index";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, FieldError, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import { useSysCfgInRoomsQuery, useSysCfgRawQuery } from "@/api/hooks/sys-cfg-hooks";
import { BaseAutocomplete } from "@/components/a-autocomplete";
import { formInitState } from "@/components/config-modals/clusterizer-edit-modal/constants";
import {
  EditClusteringType,
  EditedClusteringType,
} from "@/components/config-modals/clusterizer-edit-modal/types";
import { gameType } from "@/components/config-modals/router-edit-modal/constants";
import CustomSelectOption from "@/components/custom-select-option";
import { ModalActionsButtons } from "@/components/modal-actions-buttons";
import { isOwner } from "@/lib/auth-predicates";
import { ROOM_CONFIGS_NAMES, ROOM_NAMES } from "@/lib/constants";
import { SysCfgRawDto } from "@/shared/api";
import { meStore } from "@/store/store";

export default function ClusteringTypeEditModal({
  open,
  mode = "add",
  initialData,
  onSave,
  onCancel,
  disabled = false,
  confirmLoading = false,
}: {
  open: boolean;
  mode?: "add" | "edit";
  initialData?: EditClusteringType;
  onSave?: (data: EditedClusteringType) => void;
  onCancel?: () => void;
  confirmLoading?: boolean;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const { data: authGroupsRs } = useAuthGroupsQuery();
  const [me] = useAtom(meStore);

  const clusteringTypeSchema = z.object({
    name: z //TODO: move to Back-end*/
      .string()
      .optional()
      .transform((val) => {
        const trimmed = val?.trim();
        return trimmed && trimmed !== "" ? trimmed : `clustering_type_${Date.now()}`;
      }),
    authGroups: isOwner(me)
      ? z.array(z.string())
      : z.array(z.string()).min(1, t("mustContainsGroup")),
    enabled: z.boolean().default(true),
    data: z.object({
      comment: z.string().min(1, t("fieldRequired")),
      game_group: z.array(z.string()).min(1, t("fieldRequired")),
      game_type: z.array(z.string()).min(1, t("fieldRequired")),
      limit: z.array(z.number().int("only integers")).min(1, t("fieldRequired")),
      pattern: z.array(z.string()).min(1, t("fieldRequired")), // clustering_pattern
      ante: z.array(z.number({ invalid_type_error: t("enterOnlyNumber") })).optional(),
      pre_flop: z.array(z.string()).optional(),
      post_flop: z.array(z.string()).optional(),
      straddle: z.boolean(),
    }),
  });

  type ClusteringTypeSchemaType = z.infer<typeof clusteringTypeSchema>;

  const defaultValues = useMemo<EditedClusteringType>(() => {
    let authGroups = initialData?.auth_groups ? Array.from(initialData.auth_groups) : [];

    if (authGroupsRs?.data.length === 1 && !isOwner(me)) {
      authGroups = authGroupsRs.data.map((it) => it.name);
    }

    return initialData
      ? {
          ...initialData,
          enabled: true,
          authGroups: authGroups,
        }
      : {
          ...formInitState,
          authGroups: authGroups,
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, /* authGroupsRs,*/ me]); //TODO: fix authGroupsRs. Because of authGroupsRs default values got broken by tab switching

  const { data: clusteringPatternsRs, refetch } = useSysCfgRawQuery(
    {
      domain: ROOM_NAMES.SERVICES,
      config_name: ROOM_CONFIGS_NAMES.CLUSTERING_PATTERN,
    },
    {
      refetchOnMount: true,
    }
  );

  const { data: gameGroupsBaseRs } = useSysCfgInRoomsQuery(
    {
      configName: ROOM_CONFIGS_NAMES.GAME_GROUP,
    },
    {
      refetchOnMount: true,
    }
  );

  //TODO Remove useEffect and return config names with their rooms from back-end
  const [gameGroupsRs, setGameGroupsRs] = useState<SysCfgRawDto[]>([]);
  useEffect(() => {
    setGameGroupsRs(
      Array.from(new Set(gameGroupsBaseRs?.data?.map((it) => it.name) ?? [])).map((it) => ({
        name: it,
      }))
    );
  }, [gameGroupsBaseRs]);

  const {
    register,
    setValue,
    control,
    reset,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ClusteringTypeSchemaType>({
    resolver: zodResolver(clusteringTypeSchema),
    mode: "all",
    disabled: disabled,
    defaultValues: { ...defaultValues },
  });

  useEffect(() => {
    reset({ ...defaultValues });
  }, [defaultValues, open, reset]);

  const onSubmit: SubmitHandler<EditedClusteringType> = (data) => {
    if (!onSave) return;
    onSave(data);

    onCancel && onCancel(); //purifying form and close dialog
  };

  return (
    <>
      <Dialog
        open={open}
        key="Clusterig type modal"
        onClose={(event, reason) => {
          if (
            reason === "backdropClick" &&
            isDirty &&
            !window.confirm("Data has been changed. Do you want discard changes?")
          )
            return; //ask question if changes==true and click outSide
          onCancel?.();
        }}
      >
        <DialogTitle>
          {mode === "add" ? t("addClusteringType") : t("editClusteringType")}
        </DialogTitle>
        <DialogContent>
          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              width: "500px",
            }}
          >
            <input key="dialog-cl-type-name" type="hidden" {...register("name")} />

            <Controller
              name="authGroups"
              control={control}
              rules={{
                required: !isOwner(me) ? t("fieldRequired") : undefined,
              }}
              render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                <BaseAutocomplete
                  multiple
                  options={authGroupsRs?.data.map((it) => it.name) ?? []}
                  value={value}
                  disabled={disabled || (authGroupsRs?.data.length === 1 && !isOwner(me))}
                  onChange={(_, newValue) => {
                    onChange(newValue);
                    setValue("authGroups", newValue); // Update the value in react-hook-form
                  }}
                  renderOption={(props, option, { selected }) => (
                    <CustomSelectOption label={option} selected={selected} props={props} />
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label={t("authGroups")}
                      placeholder={t("authGroups")}
                      inputRef={ref}
                      onBlur={onBlur} // Ensure onBlur is called to update the form state
                      error={!!error?.message}
                      helperText={error?.message}
                    />
                  )}
                  renderValue={(value, getItemProps) =>
                    value.map((option, index) => (
                      <Chip label={option} {...getItemProps({ index })} key={index} />
                    ))
                  }
                />
              )}
            />

            <TextField
              key="dialog-cl-type-comment"
              {...register("data.comment", { required: t("fieldRequired") })}
              label={t("comment")}
              placeholder={t("comment")}
              variant="standard"
              error={!!errors.data?.comment}
              helperText={!!errors.data?.comment && errors.data?.comment.message}
              disabled={disabled}
            />

            <Controller
              name="data.game_group"
              control={control}
              rules={{ required: t("fieldRequired") }}
              render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                <BaseAutocomplete
                  multiple
                  freeSolo
                  value={value}
                  disabled={disabled}
                  disableCloseOnSelect
                  onChange={(_event, newValue) => {
                    //const newIds = newValue.map(item => item.name);
                    onChange(newValue);
                    setValue("data.game_group", newValue);
                  }}
                  options={gameGroupsRs?.map((el) => el.name) ?? []}
                  getOptionLabel={(option) => option}
                  //value={gameGroupsRs?.filter(option => value.includes(option.name))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t("gameGroups")}
                      placeholder={t("addTag")}
                      inputRef={ref}
                      onBlur={onBlur} // Ensure onBlur is called to update the form state
                      error={!!error?.message}
                      helperText={error?.message}
                    />
                  )}
                  renderOption={(props, option, { selected }) => {
                    const { ...optionProps } = props;
                    return (
                      <li {...optionProps}>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selected}
                        />
                        {option}
                      </li>
                    );
                  }}
                  renderValue={(value, getItemProps) =>
                    value.map((option, index) => (
                      <Chip label={option} {...getItemProps({ index })} key={index} />
                    ))
                  }
                />
              )}
            />

            <Controller
              name="data.game_type"
              control={control}
              rules={{
                required: t("fieldRequired"),
              }}
              render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                <BaseAutocomplete
                  multiple
                  freeSolo
                  options={gameType.map((el) => el.value)}
                  value={value}
                  disabled={disabled}
                  onChange={(_event, newValue) => {
                    onChange(newValue);
                    setValue("data.game_type", newValue); // Update the value in react-hook-form
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t("gameType")}
                      placeholder={t("addTag")}
                      inputRef={ref}
                      onBlur={onBlur} // Ensure onBlur is called to update the form state
                      error={!!error?.message}
                      helperText={error?.message}
                    />
                  )}
                  renderValue={(value, getItemProps) =>
                    value.map((option, index) => (
                      <Chip label={option} {...getItemProps({ index })} key={index} />
                    ))
                  }
                />
              )}
            />

            <Controller
              name="data.limit"
              control={control}
              rules={{
                required: t("fieldRequired"),
                validate: (v) =>
                  v.filter((a) => isNaN(a)).length === 0 || t("enterOnlyNumber"),
                pattern: {
                  value: /^[0-9]+$/,
                  message: t("enterOnlyNumber"),
                },
              }}
              render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                <BaseAutocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={value}
                  disabled={disabled}
                  onChange={(_event, newValue) => {
                    onChange(newValue);

                    setValue(
                      "data.limit",
                      newValue.map((v) => parseInt(v.toString()))
                    ); // Update the value in react-hook-form
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t("limit")}
                      placeholder={t("addTag")}
                      inputRef={ref}
                      onBlur={onBlur} // Ensure onBlur is called to update the form state
                      error={!!error?.message}
                      helperText={error?.message}
                      // inputProps={{inputMode:"numeric", pattern:"[0-9]*", title: "Enter only digits"}}
                    />
                  )}
                  renderValue={(value, getItemProps) =>
                    value.map((option, index) => (
                      <Chip label={option} {...getItemProps({ index })} key={index} />
                    ))
                  }
                />
              )}
            />

            <Controller
              name="data.limit"
              control={control}
              rules={{
                required: t("fieldRequired"),
                validate: (v) =>
                  v?.filter((a) => isNaN(a)).length === 0 || t("enterOnlyNumber"),
                pattern: {
                  value: /^[0-9]+$/,
                  message: t("enterOnlyNumber"),
                },
              }}
              render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                <BaseAutocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={value}
                  disabled={disabled}
                  onChange={(_event, newValue) => {
                    onChange(newValue);

                    setValue(
                      "data.limit",
                      newValue.map((v) => parseInt(v.toString()))
                    ); // Update the value in react-hook-form
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t("limit")}
                      placeholder={t("addTag")}
                      inputRef={ref}
                      onBlur={onBlur} // Ensure onBlur is called to update the form state
                      error={!!error?.message}
                      helperText={error?.message}
                      // inputProps={{inputMode:"numeric", pattern:"[0-9]*", title: "Enter only digits"}}
                    />
                  )}
                  renderValue={(value, getItemProps) =>
                    value.map((option, index) => (
                      <Chip label={option} {...getItemProps({ index })} key={index} />
                    ))
                  }
                />
              )}
            />

            <Controller
              name="data.pattern"
              control={control}
              //rules={{ required: t("fieldRequired") }}
              render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                <BaseAutocomplete
                  multiple
                  disabled={disabled}
                  options={clusteringPatternsRs?.data ?? []}
                  getOptionLabel={(option) => option.name}
                  value={clusteringPatternsRs?.data.filter((option) =>
                    value.includes(option.name)
                  )}
                  onChange={(_event, newValue) => {
                    const newIds = newValue.map((item) => item.name);
                    onChange(newIds);
                    setValue("data.pattern", newIds);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t("pattern")}
                      placeholder={t("addTag")}
                      inputRef={ref}
                      onBlur={onBlur} // Ensure onBlur is called to update the form state
                      onFocus={() => refetch()}
                      error={!!error?.message}
                      helperText={error?.message}
                    />
                  )}
                  renderValue={(value, getItemProps) =>
                    value.map((option, index) => (
                      <Chip label={option.name} {...getItemProps({ index })} key={index} />
                    ))
                  }
                />
              )}
            />

            <Controller
              name="data.ante"
              control={control}
              rules={{
                required: t("fieldRequired"),
                validate: (v) =>
                  v?.filter((a) => isNaN(a)).length === 0 || t("enterOnlyNumber"),
                pattern: {
                  value: /^[0-9.]+$/,
                  message: t("enterOnlyNumber"),
                },
              }}
              render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                <BaseAutocomplete //AAutocompleteElement doesnt work with renderTags
                  multiple
                  freeSolo
                  options={[]}
                  value={value}
                  disabled={disabled}
                  onChange={(_event, newValue) => {
                    onChange(newValue);
                    setValue(
                      "data.ante",
                      newValue
                        .map((v) => Number(parseFloat(v.toString()).toFixed(2)))
                        .filter((item) => !isNaN(item))
                    ); // Update the value in react-hook-form
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t("ante")}
                      placeholder={t("addTag")}
                      inputRef={ref}
                      onBlur={onBlur} // Ensure onBlur is called to update the form state
                      error={!!error}
                      helperText={
                        (error as FieldError[] | undefined)?.filter((el) => !!el?.message)?.[0]
                          ?.message
                      }

                      // inputProps={{inputMode:"numeric", pattern:"[0-9]*", title: "Enter only digits"}}
                    />
                  )}
                  renderValue={(value, getItemProps) =>
                    value.map((option, index) => (
                      <Chip label={option} {...getItemProps({ index })} key={index} />
                    ))
                  }
                />
              )}
            />

            <Controller
              name="data.pre_flop"
              control={control}
              render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                <BaseAutocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={value}
                  disabled={disabled}
                  onChange={(_event, newValue) => {
                    onChange(newValue);
                    setValue("data.pre_flop", newValue); // Update the value in react-hook-form
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t("preflop")}
                      placeholder={t("addTag")}
                      inputRef={ref}
                      onBlur={onBlur} // Ensure onBlur is called to update the form state
                      error={!!error?.message}
                      helperText={error?.message}
                    />
                  )}
                  renderValue={(value, getItemProps) =>
                    value.map((option, index) => (
                      <Chip label={option} {...getItemProps({ index })} key={index} />
                    ))
                  }
                />
              )}
            />

            <Controller
              name="data.post_flop"
              control={control}
              render={({ field: { onChange, onBlur, value, ref }, fieldState: { error } }) => (
                <BaseAutocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={value}
                  disabled={disabled}
                  onChange={(_event, newValue) => {
                    onChange(newValue);
                    setValue("data.post_flop", newValue); // Update the value in react-hook-form
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label={t("postflop")}
                      placeholder={t("addTag")}
                      inputRef={ref}
                      onBlur={onBlur} // Ensure onBlur is called to update the form state
                      error={!!error?.message}
                      helperText={error?.message}
                    />
                  )}
                  renderValue={(value, getItemProps) =>
                    value.map((option, index) => (
                      <Chip label={option} {...getItemProps({ index })} key={index} />
                    ))
                  }
                />
              )}
            />

            <Controller
              name="data.straddle"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={t("straddle")}
                />
              )}
            />

            <ModalActionsButtons
              onCancelClick={() => onCancel && onCancel()}
              onConfirmClick={() => void 0}
              loading={confirmLoading}
            />
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
